import { useNavigate } from "@tanstack/react-router";

import { selectTenant, signOut } from "../api/session";
import { Button } from "../components/ui/button";
import { classifyProblem } from "../domain/problem";

export interface BootstrapProblemProps {
  error: unknown;
  onRetry: () => void;
}

/**
 * A `tenant-not-resolved` bootstrap failure is an OPERATOR message, full page — never
 * "not found" (API-CONTRACT.md §1.1). So is the tenant refusing this principal (a 403 that names
 * no permission: no membership, or one removed mid-session, AU-D5) and a session the API no
 * longer accepts (401): both are about who is signed in where, and both offer the way out.
 * Every other failure shows the Problem inline.
 */
export function BootstrapProblem({ error, onRetry }: BootstrapProblemProps) {
  const classified = classifyProblem(error);
  const navigate = useNavigate();

  if (classified.kind === "forbidden" && !classified.requiredPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-lg">
        <div className="max-w-[32rem]">
          <h1 className="type-section text-foreground">No access to this tenant</h1>
          <p className="type-body mt-sm text-muted-foreground">
            {classified.problem?.detail ??
              "Your account is not a member of the tenant selected on this device."}{" "}
            Choose another tenant, or ask your administrator for access.
          </p>
          <div className="mt-md flex flex-wrap gap-sm">
            <Button
              size="sm"
              onClick={() => {
                selectTenant(null);
                void navigate({ to: "/select-tenant", replace: true });
              }}
            >
              Choose another tenant
            </Button>
            <Button size="sm" variant="outline" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (classified.kind === "unauthenticated") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-lg">
        <div className="max-w-[32rem]">
          <h1 className="type-section text-foreground">Your session has ended</h1>
          <p className="type-body mt-sm text-muted-foreground">Sign in again to continue.</p>
          <Button className="mt-md" size="sm" onClick={() => void signOut()}>
            Sign in again
          </Button>
        </div>
      </div>
    );
  }

  if (classified.kind === "tenant_not_resolved") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-lg">
        <div className="max-w-[32rem]">
          <h1 className="type-section text-foreground">
            This deployment is not configured for a tenant
          </h1>
          <p className="type-body mt-sm text-muted-foreground">
            The API did not resolve a tenant for this host. Check{" "}
            <code className="type-key">VITE_API_BASE_URL</code> and the deployment's tenant mapping.
          </p>
        </div>
      </div>
    );
  }

  const problem = classified.problem;
  return (
    <div className="p-lg">
      <div className="max-w-[36rem] rounded-md border border-state-run-failed-fg/30 bg-state-run-failed-bg p-md">
        <h2 className="type-subhead text-state-run-failed-fg">
          {problem?.title ?? "The service did not answer"}
        </h2>
        {problem?.detail ? (
          <p className="type-body mt-xs text-foreground">{problem.detail}</p>
        ) : null}
        {problem?.instance ? (
          <p className="type-caption mt-xs text-muted-foreground">
            <span className="type-key">{problem.instance}</span>
          </p>
        ) : null}
        <Button className="mt-md" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}
